// Global
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';

// Local
import styles from '../CardListing.module.scss';
import clsx from 'clsx';
import { SearchRecord } from 'lib/services/models/search-response';
import { CtaFields, HeadlineFields } from 'lib/types/headlineFields';
import { BrandAndThemeProvider } from 'components/helpers/ContextProvider/BrandAndThemeContext';
import { Themes } from 'lib/themes';
import { ContentGridCard } from './ContentGridCard';
import { ListViewRow } from './ListViewRow';
import { Preamble, PreambleOrientation } from 'components/helpers/Molecules/Preamble/Preamble';
import { useContentGridSettings } from 'lib/hooks/useSiteSettings';
import { ImageAspectRatio } from 'components/helpers/Atoms/EnhancedNextImage/ImageCommon';
import {
  CardFields,
  ContentOverride,
  getImageSettingsByContentType,
  normalizeStandardImageUrl,
  resolveEffectiveOverride,
  TimedReleaseOverride,
} from '../CardListingShared';
import { getEnumValue } from 'lib/utils/sitecore/enum';
import { Pagination } from './Pagination';
import { useContentQuery } from './ContentQueryContext';
import { Sorting } from './Sorting';
import { Facets } from './Facets';
import { Item } from '@sitecore-content-sdk/nextjs';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import { useUserProfileUrls } from 'lib/hooks/useUserProfileUrls';
import useDictionary from 'lib/hooks/useDictionary';
import CtaButton from 'components/helpers/Atoms/Cta/CtaButton';
import { SvgIcon } from 'components/helpers/SvgIcon';
import { SearchAnalyticsType } from 'components/helpers/Analytics/SearchAndFilters/SearchAndFiltersAnalytics';
import { ContentGridContentResultAnalyticsConfig } from 'components/helpers/Analytics/ContentGrid/ContentGridAnalytics';

export type ContentGridCardAlignment = 'CenterAlign' | 'LeftAlign';
export type ContentGridViewType = 'CardView' | 'ListView';

export interface ContentGridCardProps {
  card: SearchRecord;
  contentOverride?: ContentOverride;
  fallbackImageUrl?: string;
  fieldsToShow: CardFields[];
  gridAspectRatio?: ImageAspectRatio;
  aspectRatio?: ImageAspectRatio;
  forceWhiteBackground?: boolean;
  additionalCssClassName?: string;
  alignment?: ContentGridCardAlignment;
  showManageFavorites?: boolean;
  showLabel?: boolean;
  searchType?: SearchAnalyticsType;
  searchAnalyticsEnabled?: boolean;
  contentResultAnalytics?: ContentGridContentResultAnalyticsConfig;
}

export interface ContentGridProps extends HeadlineFields, CtaFields {
  theme?: Themes;
  gridAspectRatio?: ImageAspectRatio;
  contentOverrides: Record<string, ContentOverride>;
  timedReleaseOverrides?: Record<string, TimedReleaseOverride>;
  fallbackImageUrl?: string;
  fieldsToShow: CardFields[];
  columns: '1' | '2' | '3' | '4';
  viewType?: ContentGridViewType;
  centerLastRow?: boolean;
  constrainedComponentWidth?: boolean;
  preambleOrientation?: PreambleOrientation;
  cardAlignment?: ContentGridCardAlignment;
  showPagination?: boolean;
  showResultCount?: boolean;
  showSorting?: boolean;
  showFacets?: boolean;
  showManageFavorites?: boolean;
  showLabel?: boolean;
  sortByOptions?: Item[];
  sortByDirections?: Item[];
  searchType?: SearchAnalyticsType;
  searchAnalyticsEnabled?: boolean;
  contentResultAnalytics?: ContentGridContentResultAnalyticsConfig;
  excludeContentGridH2TitleFromTableOfContents?: boolean;
}

export const getSkeletonCardCount = ({
  pageSize,
  totalItems,
  requestedPageIndex,
  fallbackColumns,
}: {
  pageSize?: number | null;
  totalItems?: number | null;
  requestedPageIndex?: number | null;
  fallbackColumns: number;
}) => {
  const fallbackCount = Math.max(fallbackColumns, 1);
  const resolvedPageSize = pageSize && pageSize > 0 ? pageSize : fallbackCount;
  const resolvedPageIndex = requestedPageIndex && requestedPageIndex > 0 ? requestedPageIndex : 1;
  const resolvedTotalItems = totalItems && totalItems >= 0 ? totalItems : undefined;

  if (resolvedTotalItems === undefined) {
    return Math.max(resolvedPageSize, fallbackCount);
  }

  const itemsBeforeCurrentPage = (resolvedPageIndex - 1) * resolvedPageSize;
  const remainingItems = resolvedTotalItems - itemsBeforeCurrentPage;

  if (remainingItems <= 0) {
    return fallbackCount;
  }

  return Math.min(resolvedPageSize, remainingItems);
};

export const ContentGrid = (props: ContentGridProps) => {
  /*
   * Rendering
   */
  const isEditing = useIsEditing();
  const { favoritesUrl } = useUserProfileUrls();
  const { getDictionaryValue } = useDictionary();
  const [isFacetsOpen, setIsFacetsOpen] = useState(false);
  const [isFacetsSticky, setIsFacetsSticky] = useState(false);
  const { query, searchResponse, isLoading } = useContentQuery();
  const prevBodyOverflowRef = useRef<string | undefined>(undefined);
  const previousIsFacetsOpenRef = useRef(false);
  const hasAppliedFacetsOpenFocusRef = useRef(false);
  const facetsContainerRef = useRef<HTMLDivElement | null>(null);
  const facetsMobilePanelRef = useRef<HTMLDivElement | null>(null);
  const mobileFilterButtonRef = useRef<HTMLButtonElement | null>(null);
  const mobileFacetsDialogId = useId();
  const mobileFacetsDialogTitleId = useId();

  const manageFavoritesCtaText =
    getDictionaryValue('Common.Favorites.ManageFavorites') || 'Manage favorites';

  const mobileFilterLabel = getDictionaryValue('Components.Search.Filter') || 'Filter';
  const mobileFilterDialogLabel =
    getDictionaryValue('Components.Search.FilterResults') || 'Filter Results';

  const selectedFacetsCount =
    query?.facetGroups?.reduce((acc, group) => acc + (group.facets?.length ?? 0), 0) ?? 0;

  const totalItems = searchResponse?.Count ?? 0;
  const showLabel = getDictionaryValue('Components.Search.Show') || 'Show';
  const resultsLabel = getDictionaryValue('Components.Search.Results') || 'Results';
  const focusableSelector =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

  useEffect(() => {
    const restoreBodyOverflow = () => {
      // Restore background scroll on close or unmount
      if (prevBodyOverflowRef.current !== undefined) {
        document.body.style.overflow = prevBodyOverflowRef.current;
        prevBodyOverflowRef.current = undefined;
      } else {
        document.body.style.overflow = '';
      }
    };

    const wasFacetsOpen = previousIsFacetsOpenRef.current;

    const updateStickyState = () => {
      const isDesktop = window.matchMedia('(min-width: 75rem)').matches;

      if (!isDesktop || isFacetsOpen || !facetsContainerRef.current) {
        setIsFacetsSticky(false);
        return;
      }

      setIsFacetsSticky(facetsContainerRef.current.getBoundingClientRect().top <= 0);
    };

    if (!isFacetsOpen) {
      hasAppliedFacetsOpenFocusRef.current = false;

      if (wasFacetsOpen) {
        mobileFilterButtonRef.current?.focus();
      }

      previousIsFacetsOpenRef.current = false;

      return restoreBodyOverflow;
    }

    // Prevent background scroll while facets modal is open
    prevBodyOverflowRef.current = document.body.style.overflow || undefined;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFacetsOpen(false);
        return;
      }

      if (event.key !== 'Tab') {
        return;
      }

      const dialog = facetsMobilePanelRef.current;
      if (!dialog) {
        return;
      }

      const focusableElements = Array.from(dialog.querySelectorAll<HTMLElement>(focusableSelector));
      if (focusableElements.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!dialog.contains(document.activeElement)) {
        event.preventDefault();
        firstElement.focus();
        return;
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    };

    const focusFirstElement = () => {
      if (hasAppliedFacetsOpenFocusRef.current) {
        return;
      }

      const dialog = facetsMobilePanelRef.current;
      if (!dialog) {
        return;
      }

      const firstFacetGroupTrigger = dialog.querySelector<HTMLElement>(
        '[data-facet-group-trigger="true"]'
      );
      const firstFocusableElement = dialog.querySelector<HTMLElement>(focusableSelector);
      const target = firstFacetGroupTrigger ?? firstFocusableElement ?? dialog;

      if (document.activeElement === target) {
        hasAppliedFacetsOpenFocusRef.current = true;
        return;
      }

      hasAppliedFacetsOpenFocusRef.current = true;
      target.focus();
    };

    document.addEventListener('keydown', handleKeyDown);
    const focusTimeout = window.setTimeout(focusFirstElement, 0);
    previousIsFacetsOpenRef.current = true;

    if (!props.showFacets) {
      setIsFacetsSticky(false);
    } else {
      updateStickyState();
      window.addEventListener('scroll', updateStickyState, { passive: true });
      window.addEventListener('resize', updateStickyState);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusTimeout);
      if (props.showFacets) {
        window.removeEventListener('scroll', updateStickyState);
        window.removeEventListener('resize', updateStickyState);
      }
      restoreBodyOverflow();
    };
  }, [isFacetsOpen, props.showFacets]);

  const mergedSecondaryCta = props.showManageFavorites ? (
    <>
      {props.cta2}
      <CtaLink
        url={favoritesUrl}
        text={manageFavoritesCtaText}
        ctaStyle="outline"
        ctaVariant="brand"
      />
    </>
  ) : (
    props.cta2
  );

  const resultCount = searchResponse?.Results?.length ?? 0;
  const hasNoResults =
    !isLoading && (!searchResponse || searchResponse.Count === 0 || resultCount === 0);
  if (hasNoResults && !isEditing) {
    return null;
  }

  return (
    <BrandAndThemeProvider
      theme={props.theme}
      className={clsx(styles.contentGrid, styles[`col-${props.columns}`], {
        [styles.constrainedComponentWidth]: props.constrainedComponentWidth,
      })}
      data-component="authorable/shared/card-listing/content-grid/content-grid"
      data-hide-h2-for-toc={props.excludeContentGridH2TitleFromTableOfContents ? 'true' : undefined}
    >
      <Preamble
        headlineEyebrow={props.headlineEyebrow}
        headline={props.headline}
        headlineSubhead={props.headlineSubhead}
        headlineDescription={props.headlineDescription}
        cta1={props.cta1}
        cta2={mergedSecondaryCta}
        align={props.preambleOrientation}
      >
        {/* Facets are in POC mode only */}
        {props.showFacets ? (
          <>
            <div className={styles.mobileFilterTrigger}>
              <button
                type="button"
                ref={mobileFilterButtonRef}
                className={styles.mobileFilterButton}
                onClick={() => setIsFacetsOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={isFacetsOpen}
                aria-controls={isFacetsOpen ? mobileFacetsDialogId : undefined}
              >
                <SvgIcon
                  className={styles.mobileFilterButtonIcon}
                  icon="setting-dials"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                />
                <span>{mobileFilterLabel}</span>
              </button>
              {selectedFacetsCount > 0 && (
                <span className={styles.mobileFilterBadge}>{selectedFacetsCount}</span>
              )}
            </div>
            <div className={styles.contentGridWithFacets}>
              <div
                ref={facetsContainerRef}
                className={clsx(styles.facetsContainer, {
                  [styles.facetsContainerMobileOpen]: isFacetsOpen,
                })}
              >
                <div
                  ref={facetsMobilePanelRef}
                  id={mobileFacetsDialogId}
                  className={styles.facetsMobilePanel}
                  role={isFacetsOpen ? 'dialog' : undefined}
                  aria-modal={isFacetsOpen ? true : undefined}
                  aria-labelledby={isFacetsOpen ? mobileFacetsDialogTitleId : undefined}
                  tabIndex={isFacetsOpen ? -1 : undefined}
                >
                  <div className={styles.facetsMobilePanelHeader}>
                    <h2 id={mobileFacetsDialogTitleId} className={styles.facetsMobilePanelTitle}>
                      {mobileFilterDialogLabel}
                    </h2>
                  </div>
                  <div className={styles.facetsMobileContent}>
                    <Facets
                      isSticky={isFacetsSticky}
                      searchType={props.searchType}
                      searchAnalyticsEnabled={props.searchAnalyticsEnabled}
                      renderHeading={!isFacetsOpen}
                      renderFacetGroupHeadings={!isFacetsOpen}
                    />
                  </div>
                  <div className={styles.facetsMobileActions}>
                    <CtaButton
                      className={styles.facetsMobileActionButton}
                      onClick={() => setIsFacetsOpen(false)}
                      text={`${showLabel} ${totalItems} ${resultsLabel}`}
                      ctaStyle="filled"
                      ctaComponentClass="component-feature-full-color-cta-1-color"
                    />
                  </div>
                </div>
              </div>
              <div className={styles.contentGridMainContent}>
                <ContentGridMainContent {...props} />
              </div>
            </div>
          </>
        ) : (
          <ContentGridMainContent {...props} />
        )}
      </Preamble>
    </BrandAndThemeProvider>
  );
};

function ContentGridMainContent(props: ContentGridProps) {
  /*
   * Rendering
   */

  const contentGridSettings = useContentGridSettings();
  const { query, searchResponse, isLoading } = useContentQuery();
  const contentListRef = useRef<HTMLUListElement | null>(null);
  const hasInitializedRef = useRef(false);
  const pendingPaginationFocusRef = useRef(false);
  const [isPaginationFocusActive, setIsPaginationFocusActive] = useState(false);

  const scrollCardIntoView = useCallback((scrollTarget: HTMLElement) => {
    const viewportX = Math.min(
      Math.max(scrollTarget.getBoundingClientRect().left + scrollTarget.clientWidth / 2, 1),
      window.innerWidth - 1
    );
    const stickyElements = new Set<HTMLElement>();
    const stickyOffset = document.elementsFromPoint(viewportX, 1).reduce((offset, element) => {
      if (!(element instanceof HTMLElement)) {
        return offset;
      }

      let currentElement: HTMLElement | null = element;

      while (currentElement) {
        const computedStyle = window.getComputedStyle(currentElement);
        const isStickyElement =
          computedStyle.position === 'sticky' || computedStyle.position === 'fixed';

        if (isStickyElement) {
          const rect = currentElement.getBoundingClientRect();

          if (
            rect.height > 0 &&
            rect.top <= 1 &&
            rect.bottom > 0 &&
            !stickyElements.has(currentElement)
          ) {
            stickyElements.add(currentElement);
            return offset + rect.height;
          }
        }

        currentElement = currentElement.parentElement;
      }

      return offset;
    }, 0);
    const targetTop = scrollTarget.getBoundingClientRect().top + window.scrollY;

    window.scrollTo({
      top: Math.max(targetTop - stickyOffset - 16, 0),
      behavior: 'auto',
    });
  }, []);

  const focusGridStart = useCallback(() => {
    const contentList = contentListRef.current;
    if (!contentList) {
      return;
    }

    pendingPaginationFocusRef.current = true;
    setIsPaginationFocusActive(true);

    const firstCard = contentList.querySelector('li');
    const scrollTarget = firstCard ?? contentList;

    scrollCardIntoView(scrollTarget);
  }, [scrollCardIntoView]);

  useEffect(() => {
    if (!searchResponse || !contentListRef.current) {
      return;
    }

    if (!hasInitializedRef.current) {
      // Skip focusing on the initial render
      hasInitializedRef.current = true;
      return;
    }

    if (!pendingPaginationFocusRef.current) {
      setIsPaginationFocusActive(false);
      return;
    }

    const firstCard = contentListRef.current.querySelector('li');
    if (!firstCard) {
      pendingPaginationFocusRef.current = false;
      setIsPaginationFocusActive(false);
      return;
    }

    scrollCardIntoView(firstCard);

    const focusable = firstCard.querySelector<HTMLElement>(
      'a[href], button, [tabindex]:not([tabindex="-1"])'
    );

    if (!focusable) {
      setIsPaginationFocusActive(false);
      pendingPaginationFocusRef.current = false;
      return;
    }

    const shouldShowPaginationFocus = pendingPaginationFocusRef.current;
    const clearPaginationFocus = () => {
      pendingPaginationFocusRef.current = false;
      setIsPaginationFocusActive(false);
    };

    if (shouldShowPaginationFocus) {
      focusable.addEventListener('blur', clearPaginationFocus, { once: true });
    }

    focusable.focus({ preventScroll: true });

    if (!shouldShowPaginationFocus) {
      setIsPaginationFocusActive(false);
    }

    pendingPaginationFocusRef.current = false;

    return () => {
      focusable.removeEventListener('blur', clearPaginationFocus);
    };
  }, [scrollCardIntoView, searchResponse]);

  const defaultAspectRatio = getEnumValue<ImageAspectRatio>(
    contentGridSettings?.fields?.defaultCardImageAspectRatio
  );
  const skeletonCount = getSkeletonCardCount({
    pageSize: searchResponse?.PageSize,
    totalItems: searchResponse?.Count,
    requestedPageIndex: query?.pageIndex ?? searchResponse?.PageIndex,
    fallbackColumns: Number(props.columns),
  });
  const skeletonCards = Array.from({ length: skeletonCount }, (_, index) => index);
  const isListView = props.viewType === 'ListView';

  return (
    <>
      <div className={styles.contentGridControls}>
        {(props.showSorting || props.showPagination || props.showResultCount) && (
          <Sorting
            sortByOptions={props.sortByOptions}
            showResultCount={props.showResultCount}
            showSorting={props.showSorting}
            onSortChangeStart={focusGridStart}
            searchType={props.searchType}
            searchAnalyticsEnabled={props.searchAnalyticsEnabled}
          />
        )}

        <ul
          ref={contentListRef}
          tabIndex={-1}
          data-pagination-focus-active={isPaginationFocusActive ? 'true' : undefined}
          className={clsx(styles.contentGridContent, {
            [styles[`col-${props.columns}`]]: !isListView,
            [styles.centerLastRow]: props.centerLastRow && !isListView,
            [styles.contentGridSkeletonList]: isLoading,
            [styles.listViewContent]: isListView,
          })}
        >
          {isLoading
            ? skeletonCards.map((cardIndex) =>
                isListView ? (
                  <li
                    key={`skeleton-${cardIndex}`}
                    className={clsx(styles.listViewItem, styles.contentGridSkeletonCard)}
                    aria-hidden="true"
                  >
                    <div className={styles.listViewSkeletonRow}>
                      <div className={styles.contentGridSkeletonTitle} />
                      <div className={styles.contentGridSkeletonBody} />
                      <div className={styles.contentGridSkeletonBodyShort} />
                    </div>
                  </li>
                ) : (
                  <li
                    key={`skeleton-${cardIndex}`}
                    className={clsx(
                      styles.contentGridCard,
                      styles.contentGridSkeletonCard,
                      styles[`col-${props.columns}`]
                    )}
                    aria-hidden="true"
                  >
                    <div className={styles.contentGridSkeletonCardFrame}>
                      <div className={styles.contentGridSkeletonImageWrap}>
                        <div className={styles.contentGridSkeletonImage} />
                      </div>
                      <div className={styles.contentGridSkeletonContent}>
                        <div className={styles.contentGridSkeletonMeta} />
                        <div className={styles.contentGridSkeletonTitleGroup}>
                          <div className={styles.contentGridSkeletonTitle} />
                          <div className={styles.contentGridSkeletonTitleShort} />
                        </div>
                        <div className={styles.contentGridSkeletonRatingsRow}>
                          <div className={styles.contentGridSkeletonStars} />
                          <div className={styles.contentGridSkeletonRatingCount} />
                        </div>
                        <div className={styles.contentGridSkeletonFactsRow}>
                          <div className={styles.contentGridSkeletonFact} />
                          <div className={styles.contentGridSkeletonFact} />
                          <div className={styles.contentGridSkeletonFactShort} />
                        </div>
                        <div className={styles.contentGridSkeletonBody} />
                        <div className={styles.contentGridSkeletonBodyShort} />
                        <div className={styles.contentGridSkeletonCta} />
                      </div>
                    </div>
                  </li>
                )
              )
            : searchResponse?.Results?.map((card) => {
                const override = resolveEffectiveOverride(
                  props.timedReleaseOverrides?.[card.ContentId],
                  props.contentOverrides[card.ContentId]
                );
                const { aspectRatio, forceWhiteBackground } = getImageSettingsByContentType(
                  contentGridSettings,
                  card.ContentType
                );
                card.StandardImageUrl = normalizeStandardImageUrl(
                  card.StandardImageUrl,
                  card.PageUrl
                );

                if (isListView) {
                  return (
                    <li key={card.ContentId} className={styles.listViewItem}>
                      <ListViewRow
                        card={card}
                        contentOverride={override}
                        fieldsToShow={props.fieldsToShow}
                        searchType={props.searchType}
                        searchAnalyticsEnabled={props.searchAnalyticsEnabled}
                        contentResultAnalytics={props.contentResultAnalytics}
                      />
                    </li>
                  );
                }

                return (
                  <li
                    key={card.ContentId}
                    className={clsx(styles.contentGridCard, styles[`col-${props.columns}`])}
                  >
                    <ContentGridCard
                      card={card}
                      alignment={props.cardAlignment}
                      contentOverride={override}
                      fallbackImageUrl={props.fallbackImageUrl}
                      fieldsToShow={props.fieldsToShow}
                      gridAspectRatio={props.gridAspectRatio ?? defaultAspectRatio}
                      aspectRatio={aspectRatio}
                      forceWhiteBackground={forceWhiteBackground}
                      showManageFavorites={props.showManageFavorites}
                      showLabel={props.showLabel}
                      searchType={props.searchType}
                      searchAnalyticsEnabled={props.searchAnalyticsEnabled}
                      contentResultAnalytics={props.contentResultAnalytics}
                    />
                  </li>
                );
              })}
        </ul>
        {props.showPagination && (
          <Pagination
            onPageChangeStart={focusGridStart}
            searchType={props.searchType}
            searchAnalyticsEnabled={props.searchAnalyticsEnabled}
          />
        )}
      </div>
    </>
  );
}
